import { useApps } from '@auth/useApps'
import { useTimelineFiltersStore } from '@globals/stores/useTimelineFiltersStore'
import {
  AnnouncementEmbeds,
  AwardEmbeds,
  AwardTypeEmbeds,
  CursorResult,
  IncentiveEmbeds,
  listTimelineItems,
  RecognitionEmbeds,
  TimelineItem,
} from '@kudos/http-client'
import { AppPermission } from '@shared/permissions'
import { firstValue, SearchState } from '@shared/search/state'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useShallow } from 'zustand/shallow'
import { TimelineFilterFields } from './filters/types'

export const recognitionEmbeds: RecognitionEmbeds[] = [
  'sendToAndSender',
  'qualities',
  'attachmentUrls',
  'endorsementStats',
  'reactionCounts',
  'comments',
  'recipientCount',
]

export const awardEmbeds: AwardEmbeds[] = [
  'recipientCount',
  'badgeUrls',
  'sendToAndSender',
  'comments',
  'reactionCounts',
  'endorsementStats',
  'translations',
  'qualities',
]

export const announcementEmbeds: AnnouncementEmbeds[] = [
  'attachmentUrls',
  'status',
  'readAt',
  'reactionCounts',
  'comments',
]

export const awardTypeEmbeds: AwardTypeEmbeds[] = [
  'nominationStatus',
  'attachmentUrls',
  'ownNominees',
]

export const incentiveEmbeds: IncentiveEmbeds[] = ['latestClaimants', 'badgeUrls']

export const TIMELINE_ITEMS_QK = 'timelineItemsQueryKey'

const useTimelineItems = () => {
  const [hasAppPermission] = useApps()
  const canBrowseRecognitions = hasAppPermission(
    'recognitionApp',
    AppPermission.RecognitionAppBrowse,
  )
  const queryClient = useQueryClient()
  const { timelineFilters, setTimelineFilters } = useTimelineFiltersStore(
    useShallow(state => ({
      timelineFilters: state.timelineFilters,
      setTimelineFilters: state.setTimelineFilters,
    })),
  )
  const [filters, setFilters] = useState<SearchState<TimelineFilterFields>>(timelineFilters)

  const parseTimelineFilters = (filters: SearchState<TimelineFilterFields>) => {
    const { teamIdIn, itemTypeIn } = filters

    return {
      teamIdIn,
      itemTypeIn,
    }
  }

  useEffect(() => {
    if (!canBrowseRecognitions && firstValue(timelineFilters.itemTypeIn) === 'recognition') {
      setFilters({
        ...timelineFilters,
        itemTypeIn: [],
      })
    }
  }, [canBrowseRecognitions, timelineFilters])

  const resetInfiniteQueryPagination = (): void => {
    queryClient.setQueryData(
      [TIMELINE_ITEMS_QK],
      (oldData: {
        pageParams: { pageParam: null | string }[]
        pages: CursorResult<TimelineItem>[]
      }) => {
        if (!oldData) return undefined

        return {
          pages: oldData.pages.slice(0, 1),
          pageParams: oldData.pageParams.slice(0, 1),
        }
      },
    )
  }

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, refetch, isLoading, isFetching } =
    useInfiniteQuery({
      queryKey: [TIMELINE_ITEMS_QK],
      queryFn: ({ pageParam = null }: { pageParam: null | string }) =>
        listTimelineItems({
          cursor: pageParam,
          recognitionEmbeds,
          awardEmbeds,
          announcementEmbeds,
          incentiveEmbeds,
          awardTypeEmbeds,
          ...parseTimelineFilters(filters),
        }),
      staleTime: 1000 * 60 * 1,
      getNextPageParam: lastPage => lastPage.next,
      initialPageParam: null,
      refetchOnMount: false,
    })

  useEffect(() => {
    setTimelineFilters(filters)
    resetInfiniteQueryPagination()
    refetch()
    // NOTE: SYN-6793 Ignore all exhaustive-deps warnings. Devs should try to fix (and test) when page is revisited
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.itemTypeIn, filters.teamIdIn])

  return {
    timelineData: data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    filters,
    setFilters,
    isLoading: isLoading || isFetching,
    canBrowseRecognitions,
  }
}

export default useTimelineItems
